// A title's detection rules (F-CAT-04, spec/10 §7.3): how a station recognises that the title is running, so
// play is tracked on the floor and in the session. Three rules, every one set must match: the process names
// (lower case, no `.exe` once stored — the server normalises what HQ types), a wildcard path pattern and a
// case-insensitive regex over the image path.
//
// `DetectionFields` is the editor, shared by the app stepper (`AppDialog.tsx`) and the game's Detection dialog
// here. For a game it is the game's own process, never its launcher's. A well-known game with no rules gets a
// "Use suggested: dota2.exe" from the curated defaults (`defaultDetectionFor`, `@clavian/contracts/constants`
// — the eval-free barrel); it only fills the fields, HQ still presses Save.

import type { Game } from '@clavian/api-client';
import { defaultDetectionFor } from '@clavian/contracts/constants';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Chip, Dialog, TextField, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { Notice } from '../../ui';
import { useSetTitleDetection } from './queries';
import { useProblem } from '../../api/use-problem';

/** Process names as HQ types them: comma-separated (or one per line), blanks dropped. The server normalises the rest. */
export function parseProcessNames(text: string): string[] {
  return text
    .split(/[,\n]/)
    .map((name) => name.trim())
    .filter((name) => name !== '');
}

/** The detection rules as the editor holds them: every field a string. */
export interface DetectionDraft {
  processNames: string;
  pathPattern: string;
  regex: string;
}

export function detectionDraftOf(detection: Game['detection'] | undefined): DetectionDraft {
  return {
    processNames: detection === undefined ? '' : detection.processNames.join(', '),
    pathPattern: detection?.pathPattern ?? '',
    regex: detection?.regex ?? '',
  };
}

/** A draft as the contract's `TitleDetectionInput`: blank optional rules are `null` (cleared). */
export function toDetection(draft: DetectionDraft): { processNames: string[]; pathPattern: string | null; regex: string | null } {
  const pathPattern = draft.pathPattern.trim();
  const regex = draft.regex.trim();
  return { processNames: parseProcessNames(draft.processNames), pathPattern: pathPattern === '' ? null : pathPattern, regex: regex === '' ? null : regex };
}

/** The detection editor: process names (typed, and shown as removable chips), a path pattern and a regex. */
export function DetectionFields({
  draft,
  onChange,
  processNamesHint,
}: {
  readonly draft: DetectionDraft;
  readonly onChange: (draft: DetectionDraft) => void;
  /** What the process names field says under its label (an app's example differs from a game's). */
  readonly processNamesHint: string;
}): ReactNode {
  const { t } = useTranslation('hq');
  const names = parseProcessNames(draft.processNames);
  return (
    <>
      <TextField
        label={t('catalogue.detection.processNames')}
        description={processNamesHint}
        showOptional
        value={draft.processNames}
        onChange={(event) => onChange({ ...draft, processNames: event.target.value })}
      />
      {names.length > 0 ? (
        <div className="console-detection__chips" role="group" aria-label={t('catalogue.detection.processNamesPreview')}>
          {names.map((name, index) => (
            <Chip key={`${name}-${index}`} onRemove={() => onChange({ ...draft, processNames: names.filter((_, i) => i !== index).join(', ') })}>
              {name}
            </Chip>
          ))}
        </div>
      ) : null}
      <TextField
        label={t('catalogue.detection.pathPattern')}
        description={t('catalogue.detection.pathPatternHint')}
        showOptional
        value={draft.pathPattern}
        onChange={(event) => onChange({ ...draft, pathPattern: event.target.value })}
      />
      <TextField
        label={t('catalogue.detection.regex')}
        description={t('catalogue.detection.regexHint')}
        showOptional
        value={draft.regex}
        onChange={(event) => onChange({ ...draft, regex: event.target.value })}
      />
    </>
  );
}

export function DetectionDialog({ game, open, onOpenChange }: { readonly game: Game; readonly open: boolean; readonly onOpenChange: (open: boolean) => void }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="md" dirty="auto" title={t('catalogue.detection.title')} description={game.name}>
      {open ? <DetectionForm game={game} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function DetectionForm({ game, onDone }: { readonly game: Game; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const set = useSetTitleDetection();
  const [draft, setDraft] = useState<DetectionDraft>(() => detectionDraftOf(game.detection));
  const [error, setError] = useState<string | undefined>(undefined);
  // The curated default, offered only while the game has no process names of its own and none are typed.
  const suggestion = game.detection.processNames.length === 0 ? defaultDetectionFor(game.name) : undefined;
  const offerSuggestion = suggestion !== undefined && parseProcessNames(draft.processNames).length === 0;

  const submit = () => {
    setError(undefined);
    set.mutate(
      { id: game.id, body: toDetection(draft) },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('catalogue.detection.saved', { game: game.name }) });
          onDone();
        },
        onError: (err) => setError(problems.text(err, { fallback: t('catalogue.detection.failed') })),
      },
    );
  };

  return (
    <div className="console-form">
      <p className="console-muted">{t('catalogue.detection.intro')}</p>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {game.detection.processNames.length === 0 && game.detection.pathPattern === null && game.detection.regex === null ? (
        <Notice tone="info">{t('catalogue.detection.none')}</Notice>
      ) : null}

      {offerSuggestion ? (
        <div className="console-detection__suggestion">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() =>
              setDraft({
                ...draft,
                processNames: suggestion.processNames.join(', '),
                ...(suggestion.regex === undefined ? {} : { regex: suggestion.regex }),
              })
            }
          >
            {t('catalogue.detection.suggested', { names: suggestion.processNames.map((name) => `${name}.exe`).join(', ') })}
          </Button>
          <span className="console-muted">{t('catalogue.detection.suggestedHint', { game: game.name })}</span>
        </div>
      ) : null}

      <DetectionFields draft={draft} onChange={setDraft} processNamesHint={t('catalogue.detection.processNamesHint')} />

      <div className="console-form__actions">
        <span style={{ flex: 1 }} />
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('catalogue.detection.cancel')}
        </Button>
        <Button type="button" variant="primary" busy={set.isPending} onClick={submit}>
          {t('catalogue.detection.save')}
        </Button>
      </div>
    </div>
  );
}
